import React, { useState, useEffect } from 'react';
import { TrendingUp, Target, ArrowRight, BarChart3, Search, Lightbulb, Link, Sparkles, CheckCircle } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

// Analysis data outside component to avoid recreation on every render
const analysisSteps = [
  { icon: Search, label: "Analyzing website...", color: "from-blue-400 to-cyan-500" },
  { icon: Target, label: "Mapping competitors...", color: "from-purple-400 to-pink-500" },
  { icon: BarChart3, label: "Researching market...", color: "from-green-400 to-emerald-500" },
  { icon: Lightbulb, label: "Generating strategy...", color: "from-orange-400 to-yellow-500" }
];

const finalInsights = [
  { title: "Market Position", value: "Growing SaaS market, $2.4B opportunity", icon: BarChart3 },
  { title: "Key Competitors", value: "3 direct competitors identified", icon: Target },
  { title: "Differentiation", value: "AI-first approach gives 40% edge", icon: Sparkles },
  { title: "Recommendation", value: "Focus on mid-market segment", icon: Lightbulb }
];

const VisibilityEngagementAgent = () => {
  const [step, setStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [insights, setInsights] = useState([]);

  useEffect(() => {
    let timer;
    
    // Progress through analysis steps
    if (step < analysisSteps.length) {
      timer = setTimeout(() => {
        setProgress(0);
        const progressInterval = setInterval(() => {
          setProgress(p => {
            if (p >= 100) {
              clearInterval(progressInterval);
              setTimeout(() => setStep(s => s + 1), 300);
              return 100;
            }
            return p + 2;
          });
        }, 30);
      }, 800);
    } else if (step === analysisSteps.length && insights.length < finalInsights.length) {
      // Show insights one by one
      timer = setTimeout(() => {
        setInsights(prev => [...prev, finalInsights[prev.length]]);
      }, 400);
    }

    return () => clearTimeout(timer);
  }, [step, insights.length]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-green-50/30">
      <Header />

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            AI Strategy Agent
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Watch AI analyze your business in real-time—from URL to strategy in seconds.
          </p>
        </div>

        {/* Animated Demo */}
        <div className="max-w-3xl mx-auto mb-12 md:mb-20">
          <div className="bg-white rounded-2xl md:rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
            {/* Input Section */}
            <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-4 md:p-8">
              <div className="flex items-center space-x-2 md:space-x-3 bg-white rounded-xl md:rounded-2xl px-3 md:px-6 py-3 md:py-4 shadow-lg">
                <Link className="w-4 h-4 md:w-5 md:h-5 text-gray-400 flex-shrink-0" />
                <input
                  type="text"
                  value="www.yourstartup.com"
                  disabled
                  className="flex-1 outline-none text-gray-700 font-medium bg-transparent text-sm md:text-base"
                />
                <div className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white px-3 md:px-6 py-1.5 md:py-2 rounded-lg md:rounded-xl font-semibold text-xs md:text-sm whitespace-nowrap">
                  Analyze
                </div>
              </div>
            </div>

            {/* Analysis Progress */}
            <div className="p-4 md:p-8 bg-gradient-to-b from-gray-50 to-white">
              {step < analysisSteps.length ? (
                <div className="space-y-6">
                  {analysisSteps.slice(0, step + 1).map((item, index) => {
                    const Icon = item.icon;
                    const isActive = index === step;
                    return (
                      <div key={index} className="animate-fadeIn">
                        <div className="flex items-center space-x-4 mb-3">
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center ${isActive ? 'animate-pulse' : ''}`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-gray-900">{item.label}</span>
                              {!isActive && <CheckCircle className="w-5 h-5 text-green-500" />}
                            </div>
                            {isActive && (
                              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                <div 
                                  className={`h-full bg-gradient-to-r ${item.color} transition-all duration-300 rounded-full`}
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-center mb-6">
                    <div className="bg-gradient-to-r from-green-400 to-emerald-500 text-white px-6 py-3 rounded-full font-semibold flex items-center space-x-2">
                      <CheckCircle className="w-5 h-5" />
                      <span>Analysis Complete!</span>
                    </div>
                  </div>
                  
                  {insights.map((insight, index) => {
                    const Icon = insight.icon;
                    return (
                      <div key={index} className="bg-white border border-gray-200 rounded-2xl p-6 animate-fadeIn hover:shadow-lg transition-shadow duration-300">
                        <div className="flex items-start space-x-4">
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                            <Icon className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-1">{insight.title}</h4>
                            <p className="text-gray-600">{insight.value}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-indigo-400 to-purple-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <BarChart3 className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Market Analysis</h3>
            <p className="text-gray-600 leading-relaxed">
              Understand your market landscape and growth potential
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Search className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Competitor Intel</h3>
            <p className="text-gray-600 leading-relaxed">
              Know who you're up against and what makes you different
            </p>
          </div>

          <div className="group bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl p-4 w-fit mb-6 group-hover:scale-110 transition-transform duration-300">
              <Lightbulb className="w-10 h-10 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Strategic Insights</h3>
            <p className="text-gray-600 leading-relaxed">
              Get actionable recommendations to win in your market
            </p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          <div className="text-center">
            <div className="bg-gradient-to-br from-indigo-400 to-purple-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <div className="text-4xl font-bold text-white">10x</div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Faster insights</h3>
          </div>
          <div className="text-center">
            <div className="bg-gradient-to-br from-blue-400 to-cyan-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <Target className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Data-driven strategy</h3>
          </div>
          <div className="text-center">
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl p-8 w-fit mx-auto mb-4 shadow-lg">
              <TrendingUp className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Find hidden opportunities</h3>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-r from-[#c4ffa0] to-green-400 rounded-2xl p-16 shadow-xl">
          <h3 className="text-3xl font-bold text-[#2d4a1e] mb-4">
            Ready to unlock your competitive advantage?
          </h3>
          <p className="text-lg text-[#2d4a1e]/80 mb-8 max-w-2xl mx-auto">
            Share your URL and get instant strategic insights
          </p>
          <a
            href="https://calendly.com/kolanut"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-[#2d4a1e] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#2d4a1e]/90 transition-all duration-300 hover:scale-105 shadow-lg"
          >
            Get Started
            <ArrowRight className="w-5 h-5 ml-2" />
          </a>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default VisibilityEngagementAgent;
